import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { getBootstrap } from "@/api";

const SiteContext = createContext(null);
export const useSite = () => useContext(SiteContext);

function applyAppearance(site) {
  if (!site) return;
  const a = site.appearance || {};
  const root = document.documentElement;
  const map = {
    "--brand-primary": a.primary,
    "--brand-primary-2": a.primary2,
    "--yellow": a.yellow,
    "--yellow-dark": a.yellowDark,
    "--ink": a.ink,
    "--ink-2": a.ink2,
    "--muted-ink": a.mutedInk,
    "--bg": a.bg,
    "--bg-2": a.bg2,
    "--border-c": a.border,
    "--link": a.link,
  };
  Object.entries(map).forEach(([k, v]) => { if (v) root.style.setProperty(k, v); });

  const t = site.typography || {};
  if (t.headingFont) root.style.setProperty("--font-heading", `"${t.headingFont}", "Open Sans", sans-serif`);
  if (t.bodyFont) root.style.setProperty("--font-sans", `"${t.bodyFont}", ui-sans-serif, system-ui, sans-serif`);
  if (t.lineHeight) root.style.setProperty("--body-line-height", t.lineHeight);
  // dynamically load chosen fonts
  const families = [t.headingFont, t.bodyFont].filter(Boolean).map((f) => f.replace(/ /g, "+") + ":wght@400;500;600;700");
  if (families.length) {
    const id = "ekta-dyn-fonts";
    let link = document.getElementById(id);
    const href = `https://fonts.googleapis.com/css2?${families.map((f) => `family=${f}`).join("&")}&display=swap`;
    if (!link) { link = document.createElement("link"); link.id = id; link.rel = "stylesheet"; document.head.appendChild(link); }
    link.href = href;
  }
}

// Resolve backend media paths to absolute URLs; leave data: URIs and external URLs untouched.
function resolveUrl(u) {
  if (!u) return "";
  if (u.startsWith("data:") || u.startsWith("http")) return u;
  const base = process.env.REACT_APP_BACKEND_URL || "";
  return u.startsWith("/api/") ? `${base}${u}` : u;
}

const PLACEHOLDER_ICON =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 64 64'%3E%3Crect width='64' height='64' rx='12' fill='%23212146'/%3E%3Ctext x='32' y='44' font-family='Arial,sans-serif' font-size='36' font-weight='700' fill='%23f5c518' text-anchor='middle'%3EE%3C/text%3E%3C/svg%3E";

// Apply CMS-driven Website Identity to the document head: favicon, apple-touch-icon and base tab title.
function applyIdentity(site) {
  if (!site) return;
  const setLink = (rel, href) => {
    if (!href) return;
    let el = document.querySelector(`link[rel="${rel}"]`);
    if (!el) { el = document.createElement("link"); el.rel = rel; document.head.appendChild(el); }
    el.href = href;
  };
  const favicon = resolveUrl(site.favicon_url) || PLACEHOLDER_ICON;
  const touch = resolveUrl(site.touch_icon_url) || favicon;
  setLink("icon", favicon);
  setLink("apple-touch-icon", touch);
  // Base tab title (react-helmet still overrides this per-page where a title is provided).
  const baseTitle = site.tab_title || (site.seo_defaults && site.seo_defaults.default_title) || site.brand_name;
  if (baseTitle) document.title = baseTitle;
}

function injectAnalytics(site) {
  const an = (site && site.analytics) || {};
  if (an.gsc_verification && !document.querySelector('meta[name="google-site-verification"]')) {
    const m = document.createElement("meta"); m.name = "google-site-verification"; m.content = an.gsc_verification; document.head.appendChild(m);
  }
  window.dataLayer = window.dataLayer || [];
  if (an.gtm_id && !window.__gtm_loaded) {
    window.__gtm_loaded = true;
    const s = document.createElement("script");
    s.innerHTML = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','${an.gtm_id}');`;
    document.head.appendChild(s);
  }
  if (an.ga4_id && !window.__ga_loaded) {
    window.__ga_loaded = true;
    const s = document.createElement("script"); s.async = true; s.src = `https://www.googletagmanager.com/gtag/js?id=${an.ga4_id}`; document.head.appendChild(s);
    window.gtag = function () { window.dataLayer.push(arguments); };
    window.gtag("js", new Date());
    window.gtag("config", an.ga4_id);
  }
}

export function SiteProvider({ children }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const d = await getBootstrap();
      setData(d);
      applyAppearance(d.site);
      applyIdentity(d.site);
      injectAnalytics(d.site);
    } catch (e) {
      console.error("bootstrap failed", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <SiteContext.Provider value={{ ...(data || {}), loading, reload: load }}>
      {children}
    </SiteContext.Provider>
  );
}
